<template>
  <v-dialog
    :value="value"
    max-width="420px"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-toolbar
        flat
        color="transparent"
      >
        {{ parentName }} と連結する卓
      </v-toolbar>
      <v-card-text class="pb-2">
        <div class="text-caption text--secondary mb-2">
          同じホールの空いている卓を選択します。連結中は選んだ卓で新規入店を受け付けません。
        </div>

        <v-list dense>
          <v-list-item
            v-for="slot in slots"
            :key="`table-group-slot-${slot.tableNumber}`"
            :disabled="slot.disabled"
            :ripple="false"
            @click="toggle(slot)"
          >
            <v-list-item-action class="mr-3">
              <v-icon :color="slot.selected ? 'primary' : ''">
                {{ slot.selected ? icons.mdiCheckboxMarked : icons.mdiCheckboxBlankOutline }}
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ slot.name }}</v-list-item-title>
            </v-list-item-content>
            <span
              v-if="slot.reason"
              class="text-caption"
              :class="slot.reasonClass"
            >
              {{ slot.reason }}
            </span>
          </v-list-item>
        </v-list>

        <div class="d-flex align-center text-caption text--secondary mt-1">
          <span
            class="table-group-dialog__swatch mr-2"
            :style="{ backgroundColor: previewColor }"
          />
          グループ色は自動で割り当てます
        </div>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          v-if="existingGroup"
          text
          color="error"
          class="mr-auto"
          :disabled="isSubmitting"
          @click="release"
        >
          連結を解除
        </v-btn>
        <v-btn
          text
          :disabled="isSubmitting"
          @click="$emit('input', false)"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :loading="isSubmitting"
          :disabled="selectedNumbers.length === 0"
          @click="submit"
        >
          <v-icon
            left
            small
          >
            {{ icons.mdiLinkVariant }}
          </v-icon>
          {{ selectedNumbers.length }}卓を連結
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref,
  computed,
  watch,
  getCurrentInstance,
} from '@vue/composition-api'
import { mdiCheckboxMarked, mdiCheckboxBlankOutline, mdiLinkVariant } from '@mdi/js'
import TableGroupApi from '@/api/waiter/TableGroup'
import { tableGroupColor, TABLE_GROUP_COLORS } from '@/utils/tableGroupColors'

// 親卓に連結する子卓(空卓スロット)を選ぶダイアログ。
// 稼働中の卓・他グループの卓は選べない(サーバ側でも同じ検証をする)。
export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // 親卓(JSONAPIのtable data)
    parentTable: {
      type: Object,
      default: null,
    },
    // 親卓が属するホール(JSONAPIのhall data。attributes.tables / tableGroups / tableLayouts を参照)
    hall: {
      type: Object,
      default: null,
    },
    // 親卓の現役グループ(編集時)。null なら新規作成
    existingGroup: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy
    const selectedNumbers = ref([])
    const isSubmitting = ref(false)

    const parentNumber = computed(() => Number(props.parentTable?.attributes?.tableNumber))

    const tableName = tableNumber => {
      const attrs = props.hall?.attributes || {}
      const custom = (attrs.tableLayouts || []).find(l => Number(l.table_number) === Number(tableNumber))?.name

      return custom || `${attrs.tablePrefix || ''}${tableNumber}${attrs.tableSuffix || '卓'}`
    }

    const parentName = computed(() => (Number.isFinite(parentNumber.value) ? tableName(parentNumber.value) : ''))

    const groups = computed(() => props.hall?.attributes?.tableGroups?.data || [])
    const activeTables = computed(() => props.hall?.attributes?.tables?.data || [])

    const slots = computed(() => {
      const count = Number(props.hall?.attributes?.tableCount) || 0

      return Array.from({ length: count }, (_, i) => i + 1)
        .filter(n => n !== parentNumber.value)
        .map(n => {
          const inUse = activeTables.value.some(t => Number(t.attributes.tableNumber) === n)
          const otherGroup = groups.value.find(g => (
            g.id !== props.existingGroup?.id
            && (Number(g.attributes.parentTableNumber) === n || (g.attributes.tableNumbers || []).some(m => Number(m) === n))
          ))

          let reason = ''
          let reasonClass = 'text--disabled'
          if (inUse) {
            reason = '利用中'
            reasonClass = 'table-group-dialog__reason--in-use'
          } else if (otherGroup) {
            reason = `${tableName(otherGroup.attributes.parentTableNumber)}と連結中`
          }

          return {
            tableNumber: n,
            name: tableName(n),
            disabled: inUse || !!otherGroup,
            selected: selectedNumbers.value.includes(n),
            reason,
            reasonClass,
          }
        })
    })

    const previewColor = computed(() => {
      if (props.existingGroup) return tableGroupColor(props.existingGroup.attributes.colorIndex)
      const used = groups.value.map(g => Number(g.attributes.colorIndex))
      const free = TABLE_GROUP_COLORS.findIndex((_, i) => !used.includes(i))

      return tableGroupColor(free === -1 ? used.length : free)
    })

    const reset = () => {
      selectedNumbers.value = [...(props.existingGroup?.attributes?.tableNumbers || [])].map(Number)
    }
    watch(() => props.value, opened => { if (opened) reset() }, { immediate: true })

    const toggle = slot => {
      if (slot.disabled) return
      if (slot.selected) {
        selectedNumbers.value = selectedNumbers.value.filter(n => n !== slot.tableNumber)
      } else {
        selectedNumbers.value = [...selectedNumbers.value, slot.tableNumber].sort((a, b) => a - b)
      }
    }

    const submit = async () => {
      if (!props.parentTable || selectedNumbers.value.length === 0) return
      isSubmitting.value = true
      try {
        const payload = { tableNumbers: selectedNumbers.value }
        const res = props.existingGroup
          ? await TableGroupApi.updateTableGroup(props.parentTable.id, payload)
          : await TableGroupApi.createTableGroup(props.parentTable.id, payload)
        emit('saved', res.data.data)
        emit('input', false)
      } catch (e) {
        vm.$toast.error(e?.response?.data?.message || '卓の連結に失敗しました')
      } finally {
        isSubmitting.value = false
      }
    }

    const release = async () => {
      if (!props.parentTable || !props.existingGroup) return
      isSubmitting.value = true
      try {
        await TableGroupApi.deleteTableGroup(props.parentTable.id)
        emit('released', props.existingGroup)
        emit('input', false)
      } catch (e) {
        vm.$toast.error(e?.response?.data?.message || '連結の解除に失敗しました')
      } finally {
        isSubmitting.value = false
      }
    }

    return {
      selectedNumbers,
      isSubmitting,
      parentName,
      slots,
      previewColor,
      toggle,
      submit,
      release,
      icons: {
        mdiCheckboxMarked,
        mdiCheckboxBlankOutline,
        mdiLinkVariant,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.table-group-dialog__swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
}

.table-group-dialog__reason--in-use {
  color: #d74a4a !important;
}
</style>
